import { countLabel } from '@/utils/plural'
import { PublicSectionGate } from '@components/PublicPage'
import EmptyState from '@components/EmptyState'
import { usePublicAccommodations } from '@hooks'
import { formatUtcInstant, parseUtcInstant } from '@lib/datetime'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
	AccommodationCard,
	AccommodationGrid,
	AccommodationName,
	DateBlock,
	DateLabel,
	DateRow,
	DateValue,
	GuestCount,
	GuestRow,
} from '../Accommodations/AccommodationsPage.styles'
import PublicSectionLayout from './PublicSectionLayout'
import { publicAccommodationDetailPath } from './route'

/**
 * Public, read-only accommodations at `/shared/trips/:tripCode/accommodations`.
 * Reuses the member `AccommodationsPage.styles` cards verbatim, minus every
 * edit affordance (no add button, no "You're a guest" pill — there is no
 * viewer identity publicly). Cards click through to the read-only stay detail
 * page. Guests appear as a count only; the detail endpoint carries user ids,
 * not the member list needed to resolve avatars. Gating is the shared
 * `PublicSectionGate`.
 */
const PublicAccommodationsPage: React.FC = () => {
	const { tripCode } = useParams<{ tripCode: string }>()
	const navigate = useNavigate()
	const query = usePublicAccommodations(tripCode)

	return (
		<PublicSectionGate
			query={query}
			loadingLabel="Loading accommodations…"
			unavailableTitle="Accommodations Unavailable"
			unavailableMessage="These accommodations don't exist or haven't been shared publicly by the trip owner."
		>
			{data => {
				const accommodations = [...data.accommodations].sort(
					(a, b) =>
						parseUtcInstant(a.checkInDate).getTime() -
						parseUtcInstant(b.checkInDate).getTime(),
				)
				return (
					<PublicSectionLayout
						tripCode={tripCode ?? ''}
						title="Accommodations"
						subtitle={`${countLabel(accommodations.length, 'place')}`}
					>
						{accommodations.length === 0 ? (
							<EmptyState
								art="accommodations"
								title="No accommodations yet"
								description="No accommodations have been added to this trip yet."
							/>
						) : (
							<AccommodationGrid>
								{accommodations.map(accommodation => {
										const guestCount = accommodation.guests?.length ?? 0
										return (
											<AccommodationCard
												key={accommodation.id}
												onClick={() =>
													navigate(publicAccommodationDetailPath(tripCode ?? '', accommodation.id))
												}
											>
												<AccommodationName>{accommodation.name}</AccommodationName>

												<DateRow>
													<DateBlock>
														<DateLabel>Check-in</DateLabel>
														<DateValue>
															{formatUtcInstant(accommodation.checkInDate, 'datetime')}
														</DateValue>
													</DateBlock>
													<DateBlock $align="right">
														<DateLabel>Check-out</DateLabel>
														<DateValue>
															{formatUtcInstant(accommodation.checkOutDate, 'datetime')}
														</DateValue>
													</DateBlock>
												</DateRow>

												<GuestRow>
													<GuestCount>
														{countLabel(guestCount, 'guest')}
													</GuestCount>
												</GuestRow>
											</AccommodationCard>
										)
									})}
								</AccommodationGrid>
						)}
					</PublicSectionLayout>
				)
			}}
		</PublicSectionGate>
	)
}

export default PublicAccommodationsPage
